import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientes, createCliente, getSeguimientoAnualPendiente, registrarReunionAnual, createSeguimiento, getSeguimientosByCliente } from '../services/api';
import Modal from '../components/ui/Modal';
import { Phone, PhoneOff } from 'lucide-react';

const EMPTY_FORM = {
  nombre: '', telefono: '', email: '', rfc: '', curp: '',
  domicilio: '', ciudad: '', codigoPostal: '', fechaNacimiento: '',
  estadoCivil: '', ocupacion: '', gustos: '', datosFamilia: '',
};

export default function Clientes() {
  const [clientes, setClientes] = useState<any[]>([]);
  const [pendientesIds, setPendientesIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [llamando, setLlamando] = useState<number | null>(null);
  const [bitacoraCliente, setBitacoraCliente] = useState<any>(null);
  const [bitacora, setBitacora] = useState<any[] | null>(null);
  const navigate = useNavigate();

  const handleLlamar = async (c: any, resultado: 'Contestada' | 'No contestó') => {
    setLlamando(c.id);
    try {
      await createSeguimiento({ tipo: 'Llamada', resultado, clienteId: c.id });
      if (bitacoraCliente?.id === c.id) openBitacora(c);
    } catch {
      alert('No se pudo registrar la llamada. Intenta de nuevo.');
    } finally {
      setLlamando(null);
    }
  };

  const openBitacora = async (c: any) => {
    setBitacoraCliente(c);
    setBitacora(null);
    const res = await getSeguimientosByCliente(c.id);
    setBitacora(Array.isArray(res.data) ? res.data : []);
  };

  const loadPendientes = () =>
    getSeguimientoAnualPendiente().then(res => setPendientesIds(new Set(res.data.map((c: any) => c.id))));

  useEffect(() => {
    const timer = setTimeout(() => {
      getClientes(search).then(res => setClientes(res.data));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { loadPendientes(); }, []);

  const handleReunionAnual = async (id: number) => {
    await registrarReunionAnual(id);
    loadPendientes();
  };

  const load = () => getClientes(search).then(res => setClientes(res.data));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createCliente({
        ...form,
        fechaNacimiento: form.fechaNacimiento || null,
        rfc: form.rfc || null,
        curp: form.curp || null,
        domicilio: form.domicilio || null,
        ciudad: form.ciudad || null,
        codigoPostal: form.codigoPostal || null,
        estadoCivil: form.estadoCivil || null,
        ocupacion: form.ocupacion || null,
        gustos: form.gustos || null,
        datosFamilia: form.datosFamilia || null,
      });
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof typeof EMPTY_FORM) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Clientes</h2>
        <button
          onClick={() => { setShowModal(true); setForm(EMPTY_FORM); setError(''); }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
        >
          + Nuevo cliente
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre, teléfono, email o RFC..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Teléfono</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">RFC</th>
              <th className="px-4 py-3 text-left">Ciudad</th>
              <th className="px-4 py-3 text-left">Agente</th>
              <th className="px-4 py-3 text-left">Seguimiento anual</th>
              <th className="px-4 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {clientes.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{c.nombre}</td>
                <td className="px-4 py-3 text-gray-600">{c.telefono}</td>
                <td className="px-4 py-3 text-gray-600">{c.email}</td>
                <td className="px-4 py-3 text-gray-600">{c.rfc || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{c.ciudad || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{c.agente?.nombre}</td>
                <td className="px-4 py-3">
                  {pendientesIds.has(c.id) ? (
                    <button
                      onClick={() => handleReunionAnual(c.id)}
                      className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium hover:bg-yellow-200"
                      title="Marcar reunión anual como realizada"
                    >
                      ⏰ Pendiente
                    </button>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Al día</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      onClick={() => handleLlamar(c, 'Contestada')}
                      disabled={llamando === c.id}
                      title="Llamada contestada"
                      className="hover:scale-110 transition-transform disabled:opacity-40 flex items-center justify-center"
                    >
                      {llamando === c.id ? '⏳' : <Phone size={16} className="text-green-600" />}
                    </button>
                    <button
                      onClick={() => handleLlamar(c, 'No contestó')}
                      disabled={llamando === c.id}
                      title="No contestó / colgó"
                      className="hover:scale-110 transition-transform disabled:opacity-40 flex items-center justify-center"
                    >
                      <PhoneOff size={16} className="text-red-600" />
                    </button>
                    <button
                      onClick={() => openBitacora(c)}
                      title="Ver bitácora"
                      className="hover:scale-110 transition-transform text-sm"
                    >
                      📋
                    </button>
                  </div>
                  <button
                    onClick={() => navigate(`/clientes/${c.id}`)}
                    className="text-accent-500 hover:underline text-xs"
                  >
                    Ver detalle
                  </button>
                </td>
              </tr>
            ))}
            {clientes.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                  No se encontraron clientes
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title="Nuevo cliente" onClose={() => setShowModal(false)} size="lg">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1">
              Datos de contacto
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
              <input type="text" {...field('nombre')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
              <input type="tel" {...field('telefono')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" {...field('email')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento</label>
              <input type="date" {...field('fechaNacimiento')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Datos fiscales / CURP
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RFC</label>
              <input type="text" {...field('rfc')} placeholder="XAXX010101000"
                maxLength={13} style={{ textTransform: 'uppercase' }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CURP</label>
              <input type="text" {...field('curp')} placeholder="XAXX010101HXXXXXX00"
                maxLength={18} style={{ textTransform: 'uppercase' }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Domicilio
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Calle y número</label>
              <input type="text" {...field('domicilio')} placeholder="Calle Morelos 123, Col. Centro"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
              <input type="text" {...field('ciudad')} placeholder="Oaxaca de Juárez"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código postal</label>
              <input type="text" {...field('codigoPostal')} placeholder="68000" maxLength={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Perfil
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado civil</label>
              <select {...field('estadoCivil')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {['Soltero','Casado','Divorciado','Viudo','Unión libre'].map(o =>
                  <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
              <input type="text" {...field('ocupacion')} placeholder="Médico, contador, empresario..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Seguimiento anual
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Gustos / intereses</label>
              <textarea {...field('gustos')} rows={2} placeholder="Deportes, viajes, hobbies..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Datos de familia</label>
              <textarea {...field('datosFamilia')} rows={2} placeholder="Cónyuge, hijos, fechas importantes..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {error && <p className="col-span-2 text-red-500 text-sm">{error}</p>}

            <div className="col-span-2 flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : 'Guardar cliente'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {bitacoraCliente && (
        <Modal title={`Bitácora — ${bitacoraCliente.nombre}`} onClose={() => setBitacoraCliente(null)}>
          {bitacora === null ? (
            <p className="text-sm text-gray-400 text-center py-4">Cargando...</p>
          ) : bitacora.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Sin seguimientos registrados</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {bitacora
                .slice()
                .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                .map((s: any) => (
                  <div key={s.id} className="border-l-4 border-l-primary-300 pl-3 py-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center">{s.tipo === 'Llamada'
                        ? (s.resultado === 'Contestada'
                          ? <Phone size={14} className="text-green-600" />
                          : s.resultado === 'No contestó'
                            ? <PhoneOff size={14} className="text-red-600" />
                            : <Phone size={14} className="text-gray-500" />)
                        : '📝'}</span>
                      <span className="font-medium text-gray-800">{s.tipo}</span>
                      {s.resultado && <span className="text-gray-500">— {s.resultado}</span>}
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(s.fecha).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                    {s.notas && <p className="text-xs text-gray-600 mt-0.5">{s.notas}</p>}
                  </div>
                ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
